import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ButtonVariants,
  ButtonSizes,
  BadgeVariants,
  Alert,
  AlertVariants,
  RowActionsMenu,
} from '@carboledger/ui';
import { MoreHorizontal } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { ROUTES } from '@/app/routes';
import { LoadingOverlay, ConfirmDialog } from '@/components/feedback';
import { DiscrepancyModal, DetailPageHeader } from '@/components/common';
import { OUTGOING_SHIPMENT_STATUS_TONE } from '@/config/statusTones';
import {
  useGetOutgoingShipmentDetailQuery,
  useDeleteOutgoingShipmentMutation,
} from '../api/outgoingShipmentsApi';
import { OUTGOING_SHIPMENT_ACTIONS } from '../constants/outgoingShipmentActions';
import { buildOutgoingShipmentDiscrepancySections } from '../constants/outgoingShipmentDiscrepancy';
import { OutgoingShipmentOverviewTabView } from '../components/OutgoingShipmentOverviewTabView';
import { LinkedOrdersTabView } from '../components/LinkedOrdersTabView';
import { ShipmentAllocationDetailsTabView } from '../components/ShipmentAllocationDetailsTabView';
import styles from './OutgoingShipmentDetailPage.module.scss';

const DetailTab = Object.freeze({
  overview: 'overview',
  orders: 'orders',
  allocations: 'allocations',
});

// Outbounds ▸ Outgoing shipments ▸ Outgoing shipment details.
export const OutgoingShipmentDetailPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, shipmentId } = useParams();
  const [activeTab, setActiveTab] = useState(DetailTab.overview);
  const [isDiscrepancyOpen, setIsDiscrepancyOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteOutgoingShipment] = useDeleteOutgoingShipmentMutation();

  const {
    data: shipment,
    isLoading,
    isError,
  } = useGetOutgoingShipmentDetailQuery({ id: shipmentId });

  const tabs = useMemo(
    () => [
      { value: DetailTab.overview, label: t('OUTGOING_SHIPMENTS_DETAIL_TAB_OVERVIEW') },
      { value: DetailTab.orders, label: t('OUTGOING_SHIPMENTS_DETAIL_TAB_ORDERS') },
      { value: DetailTab.allocations, label: t('OUTGOING_SHIPMENTS_DETAIL_TAB_ALLOCATIONS') },
    ],
    [t],
  );

  const breadcrumbItems = useMemo(
    () => [
      {
        label: t('OUTGOING_SHIPMENTS_TITLE'),
        onClick: () => navigate(ROUTES.outgoingShipments(module)),
      },
      { label: shipment?.shipmentId ?? '-' },
    ],
    [t, navigate, module, shipment?.shipmentId],
  );

  const handleOpenDiscrepancies = useCallback(() => setIsDiscrepancyOpen(true), []);
  const handleCloseDiscrepancies = useCallback(() => setIsDiscrepancyOpen(false), []);

  // TODO: the Fulfill flow lands with the Fulfillment feature.
  const handleFulfill = useCallback(
    () => navigate(ROUTES.outgoingShipmentFulfill(module, shipmentId)),
    [navigate, module, shipmentId],
  );

  const handleEdit = useCallback(() => {
    navigate(ROUTES.outgoingShipmentEdit(module, shipmentId));
  }, [navigate, module, shipmentId]);

  const handleOpenDelete = useCallback(() => setIsDeleteOpen(true), []);
  const handleCancelDelete = useCallback(() => setIsDeleteOpen(false), []);

  // The shipment is gone once this resolves, so the detail route has nothing left to render.
  const handleConfirmDelete = useCallback(async () => {
    try {
      await deleteOutgoingShipment({ module, id: shipmentId }).unwrap();
      setIsDeleteOpen(false);
      navigate(ROUTES.outgoingShipments(module));
    } catch {
      // notifyApiErrors already surfaced it.
    }
  }, [deleteOutgoingShipment, navigate, module, shipmentId]);

  const menuItems = useMemo(
    () => [
      { id: 'edit', label: t('COMMON_EDIT'), onSelect: handleEdit },
      {
        id: 'delete',
        label: t('COMMON_DELETE'),
        disabled: OUTGOING_SHIPMENT_ACTIONS.delete.disabled(shipment),
        onSelect: handleOpenDelete,
      },
    ],
    [t, handleEdit, handleOpenDelete, shipment],
  );

  const metaFields = useMemo(() => {
    if (!shipment) return [];
    return [
      { id: 'material', label: t('FIELD_MATERIAL_NAME'), value: shipment.materialName },
      { id: 'facility', label: t('FIELD_FACILITY_NAME'), value: shipment.facilityName },
      { id: 'terminal', label: t('FIELD_TERMINAL'), value: shipment.terminal },
      { id: 'vessel', label: t('FIELD_VESSEL_NAME'), value: shipment.vesselName },
      {
        id: 'status',
        label: t('FIELD_STATUS'),
        value: shipment.statusLabel,
        type: 'status',
        tone: OUTGOING_SHIPMENT_STATUS_TONE[shipment.statusCode] ?? BadgeVariants.default,
      },
      { id: 'soldTo', label: t('FIELD_SOLD_TO'), value: shipment.soldTo },
      { id: 'shippingTo', label: t('FIELD_SHIPPING_TO'), value: shipment.shippingTo },
      { id: 'dispatch', label: t('FIELD_DATE_OF_DISPATCH'), value: shipment.dateOfDispatch },
      {
        id: 'discrepancies',
        label: t('FIELD_DISCREPANCIES'),
        count: shipment.discrepancyCount,
        type: 'discrepancy',
        ariaLabel: t('ACTION_VIEW_DISCREPANCIES'),
        onClick: handleOpenDiscrepancies,
      },
    ];
  }, [t, shipment, handleOpenDiscrepancies]);

  const discrepancySections = useMemo(
    () => buildOutgoingShipmentDiscrepancySections(t, shipment),
    [t, shipment],
  );

  // computeShipmentDiscrepancies (iscc-backend) returns a 0|1 flag, not itemized discrepancies — one fixed message stands in for the missing detail list.
  const discrepancyMessage = shipment?.discrepancyCount
    ? t('OUTGOING_SHIPMENTS_DETAIL_DISCREPANCY_MESSAGE')
    : undefined;

  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !shipment) {
    return (
      <div className={styles.state}>
        <Alert variant={AlertVariants.danger} title={t('OUTGOING_SHIPMENTS_LOAD_ERROR')} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <DetailPageHeader
        breadcrumbItems={breadcrumbItems}
        title={shipment.shipmentId}
        actions={
          <>
            <Button
              variant={ButtonVariants.secondary}
              size={ButtonSizes.sm}
              onClick={handleFulfill}
            >
              {t('ACTION_FULFILL')}
            </Button>
            <RowActionsMenu
              items={menuItems}
              ariaLabel={t('COMMON_MORE_ACTIONS')}
              icon={MoreHorizontal}
            />
          </>
        }
        metaFields={metaFields}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabsAriaLabel={t('OUTGOING_SHIPMENTS_TITLE')}
      />

      <div className={styles.content}>
        {activeTab === DetailTab.overview && (
          <OutgoingShipmentOverviewTabView shipment={shipment} />
        )}

        {activeTab === DetailTab.orders && (
          <LinkedOrdersTabView shipmentId={shipmentId} shipment={shipment} />
        )}

        {activeTab === DetailTab.allocations && (
          <ShipmentAllocationDetailsTabView
            module={module}
            shipmentId={shipmentId}
            shipmentByUnit={shipment.byUnit}
            allocatedGhgSavings={shipment.allocatedGhgSavings}
          />
        )}
      </div>

      <DiscrepancyModal
        open={isDiscrepancyOpen}
        onClose={handleCloseDiscrepancies}
        referenceId={shipment.shipmentId}
        message={discrepancyMessage}
        sections={discrepancySections}
      />

      <ConfirmDialog
        open={isDeleteOpen}
        title={t('OUTGOING_SHIPMENTS_DELETE_TITLE')}
        bodyTitle={t('OUTGOING_SHIPMENTS_DELETE_BODY_TITLE', { shipmentId: shipment.shipmentId })}
        description={t('OUTGOING_SHIPMENTS_DELETE_DESCRIPTION')}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};
